import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { Download, X, Loader2, FileText, Maximize2, RotateCw } from 'lucide-react';

const isImage = (ct) => typeof ct === 'string' && ct.startsWith('image/');

// ---------------------------------------------------------------------------
// Split-screen file viewer — renders an attached image or PDF in its own
// independently-scrollable pane beside the note body. Files are streamed via
// the authenticated backend proxy (no public URLs), so we fetch the blob with
// the notesApi client and render from an object URL.
// ---------------------------------------------------------------------------
export default function NoteFileViewer({ noteId, att, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const urlRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await notesApi.get(`/notes/notes/${noteId}/attachments/${att.id}`, { responseType: 'blob' });
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
      const objUrl = URL.createObjectURL(data);
      urlRef.current = objUrl;
      setUrl(objUrl);
    } catch {
      setError('Could not load this file.');
    } finally {
      setLoading(false);
    }
  }, [noteId, att.id]);

  useEffect(() => {
    setZoom(1);
    load();
    return () => {
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [load]);

  const download = useCallback(async () => {
    try {
      const { data } = await notesApi.get(`/notes/notes/${noteId}/attachments/${att.id}`, {
        params: { download: 1 }, responseType: 'blob',
      });
      const dUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = dUrl; a.download = att.filename || 'file';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(dUrl), 4000);
    } catch { setError('Could not download the file.'); }
  }, [noteId, att.id, att.filename]);

  const image = isImage(att.content_type);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--n-canvas)]" data-testid="note-file-viewer">
      {/* Viewer header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--n-line)] bg-[var(--n-surface)] px-3 py-2">
        {image ? <Maximize2 size={14} className="text-[var(--n-accent)]" /> : <FileText size={14} className="text-[var(--n-accent)]" />}
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[var(--n-ink)]" title={att.filename}>{att.filename}</span>
        {image && (
          <>
            <button onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))} title="Zoom out"
              className="n-focus inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">−</button>
            <span className="text-[11px] tabular-nums text-[var(--n-muted-2)] w-9 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} title="Zoom in"
              className="n-focus inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">+</button>
            <button onClick={() => setZoom(1)} title="Reset zoom"
              className="n-focus inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><RotateCw size={13} /></button>
          </>
        )}
        <button onClick={download} title="Download" data-testid="note-file-viewer-download"
          className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
          <Download size={13} /> Download
        </button>
        <button onClick={onClose} title="Close viewer" aria-label="Close viewer" data-testid="note-file-viewer-close"
          className="n-focus inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
      </div>

      {/* Viewer body — independently scrollable */}
      <div className="min-h-0 flex-1 overflow-auto n-scroll">
        {loading ? (
          <div className="flex h-full items-center justify-center text-[var(--n-muted)]"><Loader2 className="animate-spin" size={22} /></div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-[var(--n-muted)]">
            <FileText size={22} /> <span className="text-sm">{error}</span>
            <div className="mt-1 flex items-center gap-3">
              <button onClick={load} data-testid="note-file-viewer-retry" className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--n-accent)] hover:underline"><RotateCw size={12} /> Retry</button>
              <button onClick={download} className="text-[12px] font-medium text-[var(--n-accent)] hover:underline">Download instead</button>
            </div>
          </div>
        ) : image ? (
          <div className="flex min-h-full items-start justify-center p-4">
            <img src={url} alt={att.filename} style={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? '100%' : 'none' }}
              className="rounded-lg shadow-[var(--n-shadow)]" />
          </div>
        ) : (
          <object data={url} type="application/pdf" className="h-full w-full" aria-label={att.filename}>
            <iframe src={url} title={att.filename} className="h-full w-full border-0" />
          </object>
        )}
      </div>
    </div>
  );
}
